/**
 * Workspace Assembler — the package's main entry.
 *
 * Pure transform: takes a `RunData` JSON blob (produced by the data-fetch
 * layer on the server) plus a local trace.zip path, and writes the canonical
 * diagnostic workspace tree to disk.
 *
 * Both the server (in-process for the v2 agent) and the canary CLI (after
 * fetching `RunData` from the new `/debug/materialize-data` endpoint and
 * downloading the trace via signed URL) call this. Workspace shape is
 * literally the same code — no drift possible.
 */

import * as fs from 'node:fs/promises';
import { computeStepWorkWindows, hostOf, NAVIGATION_ACTIONS } from './_shared.js';
import { resolveWorkspacePath, WORKSPACE_DIRS } from './paths.js';
import type { DiagnosticWorkspace } from './types.js';
import { writeManifest } from './manifest-builder.js';
import { writeReportedFailure } from './reported-failure-builder.js';
import {
  writeAssertionsAndCacheDecisions,
  writeCommittedActions,
  writeLogs,
  writeRecentRuns,
  writeThoughts,
  writeStepDiagnostics,
  writeTimelineActionsAndNavigation,
  writeTimelineSteps,
  writeVariables,
} from './jsonl-writers.js';
import { writeWorkflowDefinition, hasPreflightContent } from './workflow-writer.js';
import { writeSentryLogsAndSpans } from './sentry-writer.js';
import { writeTrace } from './trace-extractor.js';
import { writeLedger } from './ledger-builder.js';
import { writeStepRollups } from './step-rollup-builder.js';
import { writeSummary, type GreenRunAccess } from './summary-builder.js';
import type { RunData } from './run-data.js';
import { writeExecutionEvidence } from './execution-writer.js';

export interface AssembleWorkspaceInput {
  runData: RunData;
  /** Absolute path to the workspace root (must already be writable). */
  workspaceRoot: string;
  /** Absolute path to a local trace.zip, or null if unavailable. */
  traceZipPath: string | null;
  /** Absolute path to sandbox-logs.jsonl.gz, or null if unavailable. */
  sandboxLogsGzipPath?: string | null;
  /**
   * How this workspace's consumer materializes comparison runs — drives the
   * `summary.md` history hint. Defaults to `'cli'` (the CLI's behavior today);
   * the server passes `'agent-tool'` for the v2 agent's primary workspace.
   */
  greenRunAccess?: GreenRunAccess;
  /** Invoked CLI brand used in generated comparison instructions. */
  cliCommand?: string;
}

/**
 * Build the canonical workspace tree at `workspaceRoot` from `runData`.
 *
 * The caller is responsible for:
 *   - Creating `workspaceRoot` (may be empty or pre-existing)
 *   - Downloading the trace.zip and passing its path
 *   - Cleaning up `workspaceRoot` after use (server: per-job tmp; CLI: --out)
 */
export const assembleWorkspace = async (
  input: AssembleWorkspaceInput
): Promise<DiagnosticWorkspace> => {
  const {
    runData,
    workspaceRoot,
    traceZipPath,
    sandboxLogsGzipPath = null,
    greenRunAccess,
    cliCommand,
  } = input;

  // Idempotent dir tree.
  await fs.mkdir(workspaceRoot, { recursive: true, mode: 0o700 });
  for (const dir of WORKSPACE_DIRS) {
    await fs.mkdir(resolveWorkspacePath(workspaceRoot, dir), { recursive: true, mode: 0o700 });
  }

  let partial = runData.partial;
  const evidenceSources = { ...(runData.evidenceSources ?? {}) };

  await writeReportedFailure({
    workspaceRoot,
    runStatus: runData.status,
    failedStepName: runData.failedStepName,
    failedStepOrderIndex: runData.failedStepOrderIndex,
    errorMessage: runData.errorMessage,
  });

  // JSONL writers — independent dumps.
  await Promise.all([
    writeTimelineSteps(workspaceRoot, runData.steps),
    writeTimelineActionsAndNavigation(workspaceRoot, runData.timeline),
    writeAssertionsAndCacheDecisions(workspaceRoot, runData.steps),
    writeStepDiagnostics(workspaceRoot, runData.steps, runData.stepDiagnostics ?? {}),
    writeThoughts(workspaceRoot, runData.thoughts),
    writeLogs(workspaceRoot, runData.networkLogs, runData.consoleLogs),
    writeCommittedActions(workspaceRoot, runData.committedActions ?? []),
    writeVariables(workspaceRoot, runData.metadataJson, runData.steps),
    writeRecentRuns(workspaceRoot, recentRunsToWriterShape(runData.recentRuns)),
    writeWorkflowDefinition({
      workspaceRoot,
      workflowId: runData.workflowId,
      workflowName: runData.workflowName,
      nodes: runData.nodes,
      edges: runData.edges,
      preflight: runData.preflight,
    }),
  ]);

  const sentryPartial = await writeSentryLogsAndSpans({
    workspaceRoot,
    logs: runData.sentryLogs,
    spans: runData.sentrySpans,
  });
  if (sentryPartial) partial = true;
  const executionEvidence = await writeExecutionEvidence({
    workspaceRoot,
    execution: runData.execution,
    sandboxLogs: runData.sandboxLogs,
    sandboxLogsGzipPath,
  });
  if (executionEvidence.partial) partial = true;
  if (executionEvidence.partial && evidenceSources.sandboxLogs?.status === 'available') {
    evidenceSources.sandboxLogs = {
      ...evidenceSources.sandboxLogs,
      status: 'partial',
      reason: 'retained sandbox logs could not be fully materialized locally',
    };
  }

  // Composed surfaces — depend on the JSONL streams above being on disk
  // (or, more accurately, on the same in-memory event projection). Computed
  // before the trace write so the screencast filmstrip can be densified around
  // step boundaries and the failure window.
  const eventProjection = projectEvents(runData);

  // Absolute ms of each step-started/step-completed annotation — the screencast
  // writer keeps the nearest frame to each so cached/playbook sub-step
  // transitions stay visible.
  const stepBoundaryMs = eventProjection.actions
    .filter(
      (a): a is typeof a & { timestampMs: number } =>
        a.isAnnotation &&
        a.timestampMs !== null &&
        (a.target === 'step-started' || a.target === 'step-completed')
    )
    .map((a) => a.timestampMs);

  // The failed step's true work window (widened for cached/playbook steps) — kept
  // at full screencast density so the failing moment always has real pixels.
  const workWindows = computeStepWorkWindows(eventProjection.steps, eventProjection.actions);
  const failedStepProjected = runData.failedStepId
    ? eventProjection.steps.find((s) => s.id === runData.failedStepId)
    : undefined;
  const failureWindow = failedStepProjected
    ? (workWindows.get(failedStepProjected.id) ??
      (failedStepProjected.startedAtMs !== null && failedStepProjected.finishedAtMs !== null
        ? { startMs: failedStepProjected.startedAtMs, endMs: failedStepProjected.finishedAtMs }
        : null))
    : null;

  const traceResult = await writeTrace({
    workspaceRoot,
    traceZipPath,
    runStartedAtMs: runData.startedAt ? Date.parse(runData.startedAt) : null,
    stepBoundaryMs,
    failureWindow,
  });
  if (traceResult.partial) partial = true;
  if (traceResult.partial && evidenceSources.trace?.status === 'available') {
    evidenceSources.trace = {
      ...evidenceSources.trace,
      status: 'partial',
      reason: 'retained trace could not be fully reconstructed locally',
    };
  }

  await writeLedger({
    workspaceRoot,
    runId: runData.runId,
    workflowId: runData.workflowId,
    workflowName: runData.workflowName,
    runStatus: runData.status,
    runStartedAt: runData.startedAt,
    reportedFailure: {
      failedStepName: runData.failedStepName,
      failedStepOrderIndex: runData.failedStepOrderIndex,
      errorMessage: runData.errorMessage,
    },
    steps: eventProjection.steps,
    thoughts: eventProjection.thoughts,
    actions: eventProjection.actions,
    navigation: eventProjection.navigation,
    network: eventProjection.network,
    consoleLogs: eventProjection.consoleLogs,
    assertions: eventProjection.assertions,
  });

  await writeStepRollups({
    workspaceRoot,
    runStartedAt: runData.startedAt,
    steps: eventProjection.steps,
    thoughts: eventProjection.thoughts,
    actions: eventProjection.actions,
    navigation: eventProjection.navigation,
    network: eventProjection.network,
    consoleLogs: eventProjection.consoleLogs,
    assertions: eventProjection.assertions,
    screencastFrames: traceResult.screencastFrames.map((f) => ({
      timestampMs: f.timestampMs,
      pageId: f.pageId,
      file: f.file,
      keptReason: f.keptReason,
    })),
  });

  await writeSummary({
    workspaceRoot,
    runId: runData.runId,
    workflowName: runData.workflowName,
    runStatus: runData.status,
    runStartedAt: runData.startedAt,
    runFinishedAt: runData.finishedAt,
    reportedFailure: {
      failedStepId: runData.failedStepId,
      failedStepName: runData.failedStepName,
      failedStepOrderIndex: runData.failedStepOrderIndex,
      errorMessage: runData.errorMessage,
    },
    steps: eventProjection.steps.map((s) => ({
      id: s.id,
      orderIndex: s.orderIndex,
      name: s.name,
      status: s.status,
      startedAtMs: s.startedAtMs,
      durationMs: s.durationMs,
      cacheOutcome: s.cacheOutcome,
      cacheHitSource: s.cacheHitSource,
      cacheOutcomeReason: s.cacheOutcomeReason,
      cacheFailureCategory: s.cacheFailureCategory,
      variablesSet: s.variablesSet,
      thoughtCount: s.thoughtCount,
      domProbeCount: s.domProbeCount,
      errorMessage: s.errorMessage,
    })),
    // Variable keys the failing step's node config references (`$var.X`) — drives the
    // upstream-divergence lineage heuristic. Empty when no failed node / no references.
    failedStepReferencedVars: runData.failedNodeId
      ? extractReferencedVarKeys(
          runData.nodes.find((n) => n.id === runData.failedNodeId)?.configJson
        )
      : [],
    network: eventProjection.network,
    consoleLogs: eventProjection.consoleLogs,
    navigation: eventProjection.navigation,
    // Mid-run browser crashes (auto-recovered) — annotation entries on the timeline
    // carry the human label in `action` and `crash-recovery` in `annotationType`.
    crashes: runData.timeline.entries
      .filter((e) => e.isAnnotation && e.annotationType === 'crash-recovery')
      .map((e) => ({ label: e.action, relativeTimestampMs: e.timestampMs })),
    recentRuns: runData.recentRuns,
    seedRuns: runData.seedRuns ?? [],
    lastGreenComparison: runData.lastGreenComparison,
    greenRunAccess,
    cliCommand,
    execution: runData.execution,
    sandboxLogs: runData.sandboxLogs,
    sandboxLogsArtifactAvailable: executionEvidence.sandboxLogsWritten,
    evidenceSources,
  });

  await writeManifest({
    workspaceRoot,
    runId: runData.runId,
    workflowId: runData.workflowId,
    orgId: runData.orgId,
    status: runData.status,
    startedAt: runData.startedAt,
    finishedAt: runData.finishedAt,
    errorMessage: runData.errorMessage,
    workflowName: runData.workflowName,
    failedStepId: runData.failedStepId,
    failedNodeId: runData.failedNodeId,
    failedStepName: runData.failedStepName,
    traceStorageKey: runData.traceStorageKey,
    diagnosticsStorageKey: runData.diagnosticsStorageKey,
    videoStorageKey: runData.videoStorageKey,
    partial,
    evidenceSources,
  });

  return {
    root: workspaceRoot,
    runId: runData.runId,
    orgId: runData.orgId,
    materializedAt: new Date().toISOString(),
    runStartedAt: runData.startedAt,
    runStatus: runData.status,
    lastGreenRunId: runData.lastGreenComparison?.greenRunId ?? null,
    hasPreflight: hasPreflightContent(runData.preflight),
  };
};

// ============================================================================
// Event projection — same shape used by ledger + step-rollup + summary.
// ============================================================================

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Extract the `$var.<key>` variable keys referenced anywhere in a node's config.
 * Used by the upstream-divergence lineage heuristic: when the failing step depends
 * on a `$var.X` produced by an earlier extractor step, that producer is a candidate.
 * Domain-agnostic — pure data-flow over whatever the node config happens to contain.
 *
 * Mirrors the canonical `VARIABLE_PATTERN` (@chatsdet/types/macros): a reference may be
 * simple (`$var.test_id`) or step-qualified (`$var.Step2_Create.client_id`). We collect
 * EVERY dotted segment so a `variablesSet[].key` (always a simple name) matches whether
 * it is the leaf of a qualified ref or the whole of a simple one. Over-collecting a step
 * qualifier is harmless here — this only seeds candidate hints, never a verdict.
 */
const VAR_REFERENCE_RE = /\$var\.(\w+(?:\.\w+)*)/g;
const extractReferencedVarKeys = (configJson: unknown): string[] => {
  if (configJson === null || configJson === undefined) return [];
  let serialized: string;
  try {
    serialized = typeof configJson === 'string' ? configJson : JSON.stringify(configJson);
  } catch {
    // A malformed/cyclic config must not crash whole-workspace assembly — just skip the hint.
    return [];
  }
  if (!serialized) return [];
  const keys = new Set<string>();
  for (const m of serialized.matchAll(VAR_REFERENCE_RE)) {
    const path = m[1];
    if (!path) continue;
    for (const segment of path.split('.')) {
      if (segment) keys.add(segment);
    }
  }
  return [...keys];
};

const stringField = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

const normalizeTs = (ts: number | string | null | undefined): number | null => {
  if (ts === null || ts === undefined) return null;
  if (typeof ts === 'number') return ts;
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Tool names that count as a "DOM probe" — the agent reading the page directly
 * (vs. the structured snapshot) to find or verify an element. A high probe count
 * on a non-failed step is a signal it struggled to act, even when it reported
 * success. Used by the upstream-divergence churn heuristic.
 */
const DOM_PROBE_TOOL_NAMES = new Set(['browser_run_code', 'browser_evaluate']);

/** A variable a step set — projected for the upstream-divergence lineage heuristic. */
interface ProjectedVariableSet {
  key: string;
  value: string | null;
  /** 'inputValue' (configured) | 'extracted' (read from the page) | other. */
  source: string | null;
  /** True when the value was extracted from the live page (carries an extractor). */
  extracted: boolean;
}

const projectVariablesSet = (meta: Record<string, unknown>): ProjectedVariableSet[] => {
  const raw = Array.isArray(meta.variablesSet) ? meta.variablesSet : [];
  const out: ProjectedVariableSet[] = [];
  for (const c of raw) {
    if (!isPlainObject(c)) continue;
    const key = stringField(c.key) ?? stringField(c.name);
    if (!key) continue;
    const source = stringField(c.source);
    out.push({
      key,
      value: typeof c.value === 'string' ? c.value : null,
      source,
      extracted: source === 'extracted' || typeof c.extractorExpression === 'string',
    });
  }
  return out;
};

const projectEvents = (runData: RunData) => {
  const baselineMs = runData.timeline.startTimestamp
    ? Date.parse(runData.timeline.startTimestamp)
    : null;

  const thoughts = runData.thoughts.map((t) => ({
    stepId: t.stepId,
    timestampMs: Date.parse(t.createdAt),
    toolName: t.toolName,
    content: t.content,
  }));

  // Per-step churn signal: total thoughts and how many were direct DOM probes.
  // Bucketed once here so the summary's upstream-divergence heuristic can flag a
  // non-failed step that thrashed (high churn + repeated probing) yet reported success.
  const thoughtStatsByStep = new Map<string, { thoughtCount: number; domProbeCount: number }>();
  for (const t of thoughts) {
    if (!t.stepId) continue;
    const cur = thoughtStatsByStep.get(t.stepId) ?? { thoughtCount: 0, domProbeCount: 0 };
    cur.thoughtCount += 1;
    if (t.toolName && DOM_PROBE_TOOL_NAMES.has(t.toolName)) cur.domProbeCount += 1;
    thoughtStatsByStep.set(t.stepId, cur);
  }

  const steps = runData.steps.map((s) => {
    const meta = isPlainObject(s.metadataJson) ? s.metadataJson : {};
    const startedAtMs = s.startedAt ? Date.parse(s.startedAt) : null;
    const finishedAtMs = s.finishedAt ? Date.parse(s.finishedAt) : null;
    const churn = thoughtStatsByStep.get(s.id) ?? { thoughtCount: 0, domProbeCount: 0 };
    return {
      id: s.id,
      nodeId: s.nodeId,
      orderIndex: s.orderIndex,
      name: s.nodeName,
      nodeType: s.nodeType,
      status: s.status,
      startedAtMs,
      finishedAtMs,
      durationMs: startedAtMs !== null && finishedAtMs !== null ? finishedAtMs - startedAtMs : null,
      errorMessage: s.errorMessage,
      cacheOutcome: stringField(meta.cacheOutcome),
      cacheHitSource: stringField(meta.cacheHitSource),
      cacheOutcomeReason: stringField(meta.cacheOutcomeReason),
      cacheFailureCategory: stringField(meta.cacheFailureCategory),
      variablesSet: projectVariablesSet(meta),
      thoughtCount: churn.thoughtCount,
      domProbeCount: churn.domProbeCount,
      diagnostics: runData.stepDiagnostics?.[s.id],
    };
  });

  const actions = runData.timeline.entries.map((e) => ({
    action: e.action,
    target: e.target ?? null,
    url: e.url ?? null,
    timestampMs: baselineMs !== null ? baselineMs + e.timestampMs : null,
    success: e.success,
    isAnnotation: e.isAnnotation ?? false,
  }));

  const navigation: Array<{
    timestampMs: number | null;
    action: string;
    from: string | null;
    to: string | null;
    crossHost: boolean;
  }> = [];
  let prevUrl: string | null = null;
  for (const a of actions) {
    const isNav = NAVIGATION_ACTIONS.has(a.action);
    const urlChanged = a.url !== null && prevUrl !== null && a.url !== prevUrl;
    if (!isNav && !urlChanged) {
      if (a.url) prevUrl = a.url;
      continue;
    }
    navigation.push({
      timestampMs: a.timestampMs,
      action: a.action,
      from: prevUrl,
      to: a.url,
      crossHost:
        hostOf(prevUrl) !== null && hostOf(a.url) !== null && hostOf(prevUrl) !== hostOf(a.url),
    });
    if (a.url) prevUrl = a.url;
  }

  const network = runData.networkLogs.map((n) => ({
    timestampMs: n.timestampMs ?? normalizeTs(n.timestamp ?? null),
    url: n.url,
    method: n.method,
    status: n.status ?? null,
  }));

  const consoleLogs = runData.consoleLogs.map((c) => ({
    timestampMs: c.timestampMs ?? normalizeTs(c.timestamp),
    type: c.type,
    text: c.text,
  }));

  const assertions: Array<{
    stepId: string;
    timestampMs: number | null;
    condition: string | null;
    severity: string | null;
    nonBlocking: boolean;
    passed: boolean;
    reason: string | null;
  }> = [];
  for (const s of runData.steps) {
    const meta = isPlainObject(s.metadataJson) ? s.metadataJson : {};
    const raw = Array.isArray(meta.assertionResults) ? meta.assertionResults : [];
    for (const r of raw) {
      if (!isPlainObject(r)) continue;
      assertions.push({
        stepId: s.id,
        timestampMs: s.startedAt ? Date.parse(s.startedAt) : null,
        condition: stringField(r.condition),
        severity: stringField(r.severity),
        nonBlocking: Boolean(r.nonBlocking),
        passed: Boolean(r.passed),
        reason: stringField(r.reason),
      });
    }
  }

  return { steps, thoughts, actions, navigation, network, consoleLogs, assertions };
};

const recentRunsToWriterShape = (runs: ReadonlyArray<RunData['recentRuns'][number]>) =>
  runs.map((r) => ({
    id: r.testRunId,
    workflowRunId: r.workflowRunId,
    status: r.status,
    startedAt: r.startedAt,
    finishedAt: r.finishedAt,
    errorMessage: r.errorMessage,
  }));
